import React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'reducers';
import { RouteComponentProps } from 'react-router-dom';
import { Globals, Constants, Colors } from 'utils';
import { Table, Pagination, Input, Modal, User, RevisionTableRow, Textarea } from 'components';
import { CleanIcon, Menu as MenuIcons } from 'assets/icons';
import { RevisionService, StorageService } from 'services';
import ModalComments from 'components/modal-comments';
import * as Jetemit from 'jetemit';

interface Props extends RootState, RouteComponentProps {
  dispatch: any;
}

const INITIAL_STATE = {
  search: '',
  status: '',
};

class Revision extends React.Component<Props> {
  timer: any = undefined;

  state = {
    header: ['', '', '', '', ''],
    form: INITIAL_STATE,
    data: [],
    page: 1,
    last_page: 1,
    item: null,
    visible: false,
    plan_user: undefined,
    // Suscripción al evento 'revision/reload' que emite App.tsx
    // cuando el abogado acepta desde el modal global.
    unsubscriber: () => {},
    visible_cancel: false,
    cancel_reason: '',
  };

  componentDidMount() {
    this.props.dispatch({
      type: 'SET_HEADER',
      payload: { title: 'Revisión Documental', onBack: null },
    });
    this.props.dispatch({ type: 'SET_SUBHEADER', payload: { title: '' } });

    // Escuchar 'revision/reload': App.tsx lo emite cuando el abogado acepta
    // una revisión desde el modal global. Si la pantalla está montada,
    // recargamos la lista para reflejar el nuevo estado.
    this.setState({
      unsubscriber: Jetemit.on('revision/reload', () => {
        this.load(true);
      }),
    });

    this.load();
  }

  componentWillUnmount() {
    this.state.unsubscriber();
  }

  load = async (withoutLoading: boolean = false) => {
    const res: any = await RevisionService.get({
      withoutLoading,
      page: this.state.page,
      ...this.state.form,
      user_id: this.props.user?.id,
    });
    this.setState({
      data: res.revisions.rows,
      last_page: res.revisions.count,
      plan_user: res.plan_user,
    });
  };

  loadComments = async (withoutLoading: boolean = false) => {
    return await RevisionService.viewComments({
      revision_id: (this.state.item as any)?.id,
      withoutLoading,
    });
  };

  reset = () => {
    this.setState({ form: INITIAL_STATE, page: 1 }, () => this.load(true));
  };

  change = (e: any, callback = () => {}) => {
    this.setState(
      {
        page: 1,
        form: { ...this.state.form, [e.target.name]: e.target.value },
      },
      callback,
    );
  };

  openModal = (item: any) => {
    this.setState({ item }, () => this.setState({ visible: true }));
  };

  onClose = (reload: boolean = false) => {
    this.setState({ visible: false }, () => reload && this.load());
  };

  onCloseAccept = (reload: boolean = false) => {
    this.setState({ visible_accept: false, item: null }, () => reload && this.load());
  };
  getColor = (item: any) => {
    switch (item.status) {
      case Constants.REVISION_STATUS.PENDING:
        return Colors.orange;
      case Constants.REVISION_STATUS.ACCEPTED:
        return Colors.green;
      case Constants.REVISION_STATUS.REJECTED:
        return Colors.danger;
      case Constants.REVISION_STATUS.FINISHED:
        return Colors.gray3;
      case Constants.REVISION_STATUS.CANCELLED:
        return Colors.gray6;
    }
  };

  sendComments = async (item: any) => {
    await RevisionService.sendComments({
      revision_id: (this.state.item as any)?.id,
      user_id: this.props.user?.id,
      message: item.message,
      file: item.file,
      withoutLoading: true,
      hasFile: true,
    });
  };

  sendResponse = async (item: any, file: any) => {
    await RevisionService.saveResponse({
      file,
      revision_id: item.id,
      hasFile: true,
    });
    Globals.showSuccess('Se ha enviado el archivo correctamente');
    this.load(true);
  };

  finishRevision = async (item: any) => {
    Globals.confirm('¿Desea marcar la revision como finalizada?', async () => {
      try {
        await RevisionService.changeStatus({
          revision_id: item.id,
          status: Constants.REVISION_STATUS.FINISHED,
        });
        Globals.showSuccess('Se ha terminado la revision correctamente');
        this.load(true);
      } catch (error) {}
    });
  };

  openCancelRevision = (item: any) => {
    this.setState({ item, cancel_reason: '', visible_cancel: true });
  };

  closeCancelRevision = () => {
    this.setState({ visible_cancel: false, item: null, cancel_reason: '' });
  };

  cancelRevision = async () => {
    if (!this.state.cancel_reason.trim()) {
      Globals.showError('Ingrese el motivo de la cancelación');
      return;
    }
    try {
      await RevisionService.cancel({
        revision_id: (this.state.item as any)?.id,
        user_id: this.props.user?.id,
        reason: this.state.cancel_reason.trim(),
      });
      Globals.showSuccess('Revisión cancelada correctamente');
      this.closeCancelRevision();
      this.load(true);
    } catch (error: any) {
      Globals.showError(error?.response?.data?.message || 'Error al cancelar la revisión');
    }
  };

  openFile = async (key: string) => {
    const opened = await StorageService.openFile(key);
    if (!opened) Globals.showError('No se pudo obtener el archivo');
  };

  render() {
    const { visible, visible_cancel, cancel_reason } = this.state;
    const item: any = this.state.item;
    const plan_user: any = this.state.plan_user;
    const is_client = this.props.user?.level_id == Constants.LEVELS.CLIENT;

    return (
      <div id='lawyer-revision'>
        {/* Modal de comentarios */}
        <Modal
          className='modal-comments'
          visible={visible}
          title='Comentarios'
          onClose={() => this.onClose()}
        >
          {/* @ts-ignore */}
          <ModalComments
            loadComments={(withoutLoading) => this.loadComments(withoutLoading)}
            sendComments={(item) => this.sendComments(item)}
            currentUser={(this.state?.item as any)?.user}
            itemId={(this.state?.item as any)?.id}
            service={Constants.SERVICES.REVISION}
            writeComments={(this.state?.item as any)?.status == Constants.REVISION_STATUS.PENDING}
          />
        </Modal>

        <Modal
          className='modal-rating modal-cancel-call'
          visible={visible_cancel}
          title='Cancelar revisión'
          onClose={() => this.closeCancelRevision()}
        >
          <div className='container-cancel-call'>
            <p>Indique el motivo por el cual desea cancelar esta revisión documental.</p>
            <Textarea
              rows={4}
              placeholder='Motivo de la cancelación'
              value={cancel_reason}
              onChange={(e: any) => this.setState({ cancel_reason: e.target.value })}
            />
            <div className='btn-container'>
              <button className='btn btn-accept' onClick={() => this.cancelRevision()}>
                Confirmar cancelación
              </button>
            </div>
          </div>
        </Modal>

        {/* El modal de aceptar revisión ahora vive en App.tsx (global).
            Funciona desde cualquier pantalla sin importar la ruta activa. */}

        <div className='row row-filter'>
          <div className='col-md-12'>
            <div className='row'>
              <div className='col-md-4'>
                <Input
                  value={this.state.form.search}
                  name='search'
                  label='Buscar'
                  placeholder='Buscar'
                  className='input-table'
                  onSubmit={() => this.load()}
                  onChange={(e: any) => {
                    clearTimeout(this.timer);
                    this.timer = setTimeout(() => this.load(true), 1000);
                    this.change(e);
                  }}
                />
              </div>
              <div className='col-md-1'>
                {this.state.form != INITIAL_STATE && (
                  <button className='btn btn-clean' onClick={() => this.reset()}>
                    <img src={CleanIcon} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <Table
          title=''
          data={this.state.data.length}
          header={this.state.header}
          headerContent={
            <div className='container-header-table'>
              <div className='container-icon'>
                <img src={MenuIcons.Menu6} />
              </div>
              <p>Registro de orden de revisión</p>
            </div>
          }
        >
          {this.state.data.map((i: any, index: number) => {
            return (
              <RevisionTableRow
                key={index}
                item={i}
                role='lawyer'
                onOpenFile={(key) => this.openFile(key)}
                onSendResponse={(item, file) => this.sendResponse(item, file)}
                onOpenComments={(item) => this.openModal(item)}
                onFinishRevision={(item) => this.finishRevision(item)}
                onCancel={(item) => this.openCancelRevision(item)}
              />
            );
          })}
        </Table>

        <Pagination
          pages={this.state.last_page}
          active={this.state.page}
          onChange={async (page: number) => {
            await this.setState({ page });
            this.load();
          }}
        />
      </div>
    );
  }
}

export default connect((state: RootState) => {
  return { user: state.user };
})(Revision);
